import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ThemeToggleButton from "./ThemeToggleButton";
import LogoComponent from "../Reusable/LogoComponent";
import Logo from "../../assets/images/Logo.png";
import { BellDot, ChevronDown, Menu, MessageCircleMore } from "lucide-react";
import CustomPopover from "../Reusable/Popover";
import NavbarProfilePopover from "../Header/NavBarProfilePopover";
import Avatar from "../Reusable/Avatar";
import avatarImg from "../../assets/images/avatar.png";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store";
import { getUnreadThreadCount } from "../../store/thunks/threadThunks";
import socket from "../../config/socket";

const Navbar = ({
  toggleSidebar,
  isMobile,
  isSidebarOpen,
  pageTitle = "Admin Profile",
  pageTitleClassName,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuthenticated } = useSelector((state) => state.auth);
  const { unreadCount } = useSelector((state) => state.thread);
  const currentOpenThread = useSelector((state) => state.order.thread.data);

  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userName, setUserName] = useState("");

  // Fetch unread count on mount and setup socket listeners
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUnreadThreadCount());

      const handleUnreadUpdate = () => {
        dispatch(getUnreadThreadCount());
      };

      const handleReceiveMessage = (message) => {
        // Check if the message is for the currently open thread
        const messageThreadId =
          message.thread?._id || message.threadId || message.thread;
        const currentThreadId = currentOpenThread?._id;

        // If the message is NOT for the currently open thread, refetch the count
        // If it IS for the current thread, the user is looking at it, so it's not "unread" in the UI context
        if (messageThreadId !== currentThreadId) {
          dispatch(getUnreadThreadCount());
        }
      };

      socket.on("unread-count-update", handleUnreadUpdate);
      socket.on("receive-message", handleReceiveMessage);

      return () => {
        socket.off("unread-count-update", handleUnreadUpdate);
        socket.off("receive-message", handleReceiveMessage);
      };
    }
  }, [dispatch, isAuthenticated, currentOpenThread]);

  useEffect(() => {
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const admin = parsedUser.admin;
        if (admin?.firstName && admin?.lastName) {
          setUserName(`${admin.firstName} ${admin.lastName}`);
        }
      } catch (err) {
        console.error("Error parsing user data:", err);
      }
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle click outside to close menus
  useEffect(() => {
    if (!isMenuOpen) return;
    function handleClickOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isMenuOpen]);

  return (
    <header
      className={`w-full border-b border-[var(--color-stroke-01)] transition-all duration-300 px-5
    ${scrolled ? "bg-white/10 backdrop-blur-md shadow-sm" : "bg-transparent"}
    `}
    >
      <nav className="w-full h-[67px] flex items-center px-4 lg:px-8 relative">
        {/* Mobile Menu Button */}
        {isMobile && (
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-[var(--color-background-10)] transition-colors mr-4"
          >
            <Menu size={24} color="var(--color-content)" strokeWidth={1.5} />
          </button>
        )}

        {/* Dynamic Page Title - hidden on mobile */}
        <div
          className={`hidden lg:block text-lg font-semibold ${
            pageTitleClassName || "text-[var(--color-content)]"
          }`}
        >
          {pageTitle}
        </div>

        {/* Right icons */}
        <div className="flex items-center gap-3 ml-auto ">
          <ThemeToggleButton />
          {/* Desktop Icons - hidden on mobile */}
          <div className="items-center hidden gap-3 sm:flex">
            <button
              className="relative"
              onClick={() => navigate("/admin/full-chat-view")}
              title="Chat"
            >
              <MessageCircleMore
                size={28}
                color="var(--color-content)"
                strokeWidth={1.5}
                className="cursor-pointer "
              />
              {/* Notification Badge */}
              {unreadCount > 0 && (
                <span className="absolute top-0 -right-1 flex items-center justify-center w-4 h-4 bg-[var(--color-primary)] text-white text-[8px] rounded-full">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            <BellDot size={28} color="var(--color-content)" strokeWidth={1.5} />
            <div className="text-sm text-[var(--color-content)]">
              {userName || "Admin"}
            </div>
          </div>

          {/* Avatar - always visible */}
          <CustomPopover
            content={
              <div className="navbar-popover-wrapper">
                <NavbarProfilePopover
                  onLogoutClick={() => dispatch(logout())}
                  unreadCount={unreadCount}
                />
              </div>
            }
            placement="bottomRight"
            trigger={["click"]}
            arrow={false}
          >
            <Avatar
              src={avatarImg}
              alt="User Avatar"
              className="w-8 h-8 cursor-pointer"
            />
            <div className="hidden ml-4 lg:block">
              <ChevronDown
                size={20}
                color="var(--color-content)"
                strokeWidth={1.5}
              />
            </div>
          </CustomPopover>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
